import Logger from '../logger';

const logger = new Logger('StreamUtils');

/**
 * Collect all streams from a session and provided stream
 */
export const collectSessionStreams = (session, providedStream, localStream) => {
	const streams = new Set();
	if (providedStream) streams.add(providedStream);
	if (localStream) streams.add(localStream);

	if (session) {
		if (session._localMediaStream) streams.add(session._localMediaStream);
		if (session.localMediaStream) streams.add(session.localMediaStream);

		const connection = session.connection || session._connection;
		if (connection && typeof connection.getLocalStreams === 'function') {
			try {
				connection.getLocalStreams().forEach(stream => streams.add(stream));
			} catch (error) {
				logger.warn('Failed to collect streams from connection:', error);
			}
		}
	}

	return Array.from(streams).filter(Boolean);
};

/**
 * Collect tracks of a specific kind from multiple streams
 */
export const collectTracks = (streams, kind) => {
	const map = new Map();
	streams.forEach(stream => {
		if (!stream) return;
		const getter = kind === 'video' ? stream.getVideoTracks : stream.getAudioTracks;
		if (typeof getter !== 'function') return;
		try {
			getter.call(stream).forEach(track => {
				if (track && !map.has(track.id)) {
					map.set(track.id, track);
				}
			});
		} catch (error) {
			logger.warn('Failed to enumerate tracks on stream:', error);
		}
	});

	return Array.from(map.values());
};

/**
 * Remove tracks from multiple streams
 */
export const removeTracksFromStreams = (tracks, streams) => {
	streams.forEach(stream => {
		if (!stream || typeof stream.removeTrack !== 'function') return;
		tracks.forEach(track => {
			try {
				stream.removeTrack(track);
			} catch (error) {
				// Older browsers may throw if track not present; ignore
			}
		});
	});
};

/**
 * Build an audio-only stream from multiple streams
 */
export const buildAudioOnlyStream = (streams) => {
	const audioTracks = collectTracks(streams, 'audio').filter(track => track.readyState !== 'ended');
	if (audioTracks.length === 0) {
		return null;
	}

	const audioStream = new MediaStream();
	audioTracks.forEach(track => {
		try {
			audioStream.addTrack(track);
		} catch (error) {
			logger.warn('Failed to add audio track to stream:', error);
		}
	});

	return audioStream;
};

/**
 * Release a stream and stop all its tracks
 */
export const releaseStream = (stream) => {
	if (!stream) return;

	try {
		if (typeof stream.getTracks === 'function') {
			stream.getTracks().forEach(track => {
				if (track && track.readyState !== 'ended') {
					try {
						track.stop();
					} catch (error) {
						logger.warn('Failed to stop track during release:', error);
					}
				}
			});
		}

		if (typeof stream.release === 'function') {
			stream.release();
		}
	} catch (error) {
		logger.warn('Failed to release stream:', error);
	}
};


